/**
 * FOCUS MANAGEMENT SYSTEM
 * 
 * Handles focus management for accessible navigation.
 * Ensures proper focus flow when opening/closing menus and navigating levels.
 */

export class FocusManager {
  private returnFocusElement: HTMLElement | null = null;

  /**
   * Trap focus within a container
   * All focus stays within provided element
   */
  trapFocus(containerElement: HTMLElement): () => void {
    const focusableElements = this.getFocusableElements(containerElement);

    if (focusableElements.length === 0) return () => {};

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.shiftKey) {
        // Shift + Tab (backward)
        if (activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab (forward)
        if (activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    containerElement.addEventListener('keydown', handleKeyDown);

    // Return cleanup function
    return () => {
      containerElement.removeEventListener('keydown', handleKeyDown);
    };
  }

  /**
   * Get all focusable elements within a container
   */
  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(container.querySelectorAll(selector)).filter((el) => {
      const element = el as HTMLElement;
      return element.offsetParent !== null; // Visible element
    });
  }

  /**
   * Save current focus and prepare to restore later
   */
  saveFocus(): void {
    this.returnFocusElement = document.activeElement as HTMLElement;
  }

  /**
   * Restore focus to previously saved element
   */
  restoreFocus(): void {
    if (
      this.returnFocusElement &&
      this.returnFocusElement.offsetParent !== null
    ) {
      this.returnFocusElement.focus();
    }
  }

  /**
   * Move focus to first focusable element in container
   */
  focusFirstElement(container: HTMLElement): void {
    const elements = this.getFocusableElements(container);
    if (elements.length > 0) {
      elements[0].focus();
    }
  }

  /**
   * Announce text to screen readers
   */
  announce(text: string, priority: 'polite' | 'assertive' = 'polite'): void {
    let announcer = document.querySelector(
      `[role="status"][aria-live="${priority}"]`
    );

    if (!announcer) {
      announcer = document.createElement('div');
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', priority);
      announcer.className = 'sr-only'; // Screen reader only
      document.body.appendChild(announcer);
    }

    announcer.textContent = text;
  }

  /**
   * Close focus trap
   */
  releaseFocus(): void {
    this.returnFocusElement = null;
  }
}

export default FocusManager;
